import { useParams, useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { Loader2, AlertCircle, ExternalLink, Video, Layout, Users } from "lucide-react"
import { useTranslation } from "react-i18next"
import { VideoPlayer } from "@/components/common/VideoPlayer"
import { AiGeneratedNotice } from "@/components/common/AiGeneratedNotice"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher"
import { UserMenu } from "@/features/auth/components/UserMenu"
import { GeneratorSidebar } from "@/features/generator/components/GeneratorSidebar"
import { MobileSidebarDrawer } from "@/features/generator/components/MobileSidebarDrawer"
import { MobileHeader } from "@/components/layout/MobileHeader"
import { useSharedVideo } from "@/hooks/useShare"
import { useGenerateVideo } from "@/hooks/useHistory"
import { useAuthStore } from "@/stores/authStore"
import { useGeneratorStore } from "@/stores/generatorStore"
import { useAuthMe } from "@/hooks/useAuthMe"
import { useConfig } from "@/hooks/useConfig"
import { WRKS_PORTAL_URLS } from "@/lib/constants"
import { toast } from "sonner"
import type { ApplyableSettings, FrameImage } from "@/types/generation"
import { restoreTemplateParameters } from "@/features/templates/lib/restore-template-parameters"

export function SharePage() {
  const { t } = useTranslation()
  const { shareId } = useParams<{ shareId: string }>()
  const navigate = useNavigate()
  const { data: sharedDetail, isLoading, error } = useSharedVideo(shareId ?? null)
  const { isAuthenticated } = useAuthStore()
  const { data: authMe } = useAuthMe()
  // 비인증 방문자 CTA 는 이 배포의 웍스 포탈로 (gov 배포에서는 gov 웍스)
  const { data: config } = useConfig()
  const communityHidden = authMe?.menuHidden.community ?? false
  const {
    applySettings,
    prompt,
    ratio,
    resolution,
    duration,
    frameImages,
    selectedTemplate,
    isSubmitting,
    setIsSubmitting,
  } = useGeneratorStore()
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const generateMutation = useGenerateVideo()

  // 공유 비디오 데이터 로드 시 generatorStore에 설정 적용
  useEffect(() => {
    if (!sharedDetail) return

    const reusableFrameImages: FrameImage[] = (sharedDetail.generation.frameImages ?? []).map((frame, idx) => ({
      id: `reuse-${sharedDetail.generation.id}-${frame.role}-${idx}`,
      role: frame.role,
      s3Key: frame.s3Key,
      thumbnailS3Key: frame.thumbnailS3Key,
      url: sharedDetail.generation.frameImageUrls?.[idx] ?? "",
      thumbnailUrl: sharedDetail.generation.frameImageUrls?.[idx] ?? "",
    }))

    const settings: ApplyableSettings = {
      prompt: sharedDetail.generation.prompt,
      ratio: sharedDetail.generation.ratio as ApplyableSettings['ratio'],
      resolution: sharedDetail.generation.resolution as ApplyableSettings['resolution'],
      duration: sharedDetail.generation.duration,
      frameImages: reusableFrameImages.length > 0 ? reusableFrameImages : undefined,
      template: sharedDetail.generation.template ?? undefined,
    }

    // 템플릿 파라미터 칩 복원은 템플릿 조회를 기다린다. 그새 다른 공유로 바뀌면 버린다
    let cancelled = false
    void restoreTemplateParameters(settings).then((restored) => {
      if (!cancelled) applySettings(restored)
    })
    return () => {
      cancelled = true
    }
  }, [sharedDetail, applySettings])

  // 탭 클릭 시 Generator 페이지로 이동
  const handleTabChange = (tab: string) => {
    if (tab === "history") navigate("/my")
    else if (tab === "templates") navigate("/templates")
    else if (tab === "community") navigate("/community")
  }

  // 생성 버튼 클릭 핸들러
  const handleGenerate = async () => {
    if (!prompt.trim() || isSubmitting) return

    const frameImagesPayload = [
      ...(frameImages.firstFrame ? [frameImages.firstFrame] : []),
      ...(frameImages.lastFrame ? [frameImages.lastFrame] : []),
    ]

    const requestBody: Record<string, unknown> = {
      prompt,
      ratio,
      resolution,
      duration,
      frameImages: frameImagesPayload.length > 0
        ? frameImagesPayload.map(img => ({
            role: img.role,
            s3Key: img.s3Key,
            thumbnailS3Key: img.thumbnailS3Key,
            filename: img.filename,
          }))
        : undefined,
    }

    if (selectedTemplate?.id) {
      requestBody.templateId = selectedTemplate.id
    }

    setIsSubmitting(true)
    try {
      await generateMutation.mutateAsync(requestBody)
      navigate("/my")
    } catch (error: unknown) {
      const err = error as Record<string, unknown> | undefined
      const data = err?.data as Record<string, unknown> | undefined
      const message = data?.message || (err?.message as string) || t('pages.unknownError')
      toast.error(t('pages.generateFailed'), { description: String(message) })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-screen bg-slate-50 items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error || !sharedDetail) {
    return (
      <div className="relative flex h-screen bg-slate-50">
        <div className="absolute right-4 top-4">
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">
            {t('pages.shareNotFoundTitle')}
          </h1>
          <p className="text-slate-500 mb-6">
            {t('pages.shareNotFoundDescription')}
          </p>
          <Button onClick={() => navigate("/")}>{t('pages.backToHome')}</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-slate-50 overflow-hidden">
      {/* Mobile Header - lg 미만에서만 표시 */}
      <MobileHeader
        onMenuClick={() => setMobileDrawerOpen(true)}
        showUserMenu={isAuthenticated}
        className="lg:hidden"
      />

      {/* Mobile Sidebar Drawer */}
      <MobileSidebarDrawer
        open={mobileDrawerOpen}
        onOpenChange={setMobileDrawerOpen}
        disabled={!isAuthenticated}
        onGenerate={handleGenerate}
      />

      {/* Desktop Sidebar - lg 이상에서만 표시 */}
      <GeneratorSidebar
        disabled={!isAuthenticated}
        onGenerate={handleGenerate}
      />

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header - 인증 상태에 따라 다르게 표시 */}
        {isAuthenticated ? (
          // 인증된 사용자: 탭 + UserMenu
          <div className="h-14 lg:h-16 px-4 lg:px-8 shrink-0 bg-slate-50 flex items-center">
            <div className="w-full max-w-5xl mx-auto flex items-center justify-between">
              <Tabs value="" onValueChange={handleTabChange}>
                <TabsList className="w-full lg:w-auto">
                  <TabsTrigger value="history" className="flex-1 lg:flex-initial flex items-center justify-center gap-1.5 lg:gap-2 px-2 lg:px-6">
                    <Video className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('pages.tabMyVideos')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="templates" className="flex-1 lg:flex-initial flex items-center justify-center gap-1.5 lg:gap-2 px-2 lg:px-6">
                    <Layout className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('pages.tabTemplates')}</span>
                  </TabsTrigger>
                  {!communityHidden && (
                    <TabsTrigger value="community" className="flex-1 lg:flex-initial flex items-center justify-center gap-1.5 lg:gap-2 px-2 lg:px-6">
                      <Users className="w-4 h-4" />
                      <span className="hidden sm:inline">{t('pages.tabCommunity')}</span>
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
              {/* LanguageSwitcher + UserMenu - 데스크톱에서만 표시 (모바일은 MobileHeader에 있음) */}
              <div className="hidden lg:flex items-center gap-2">
                <LanguageSwitcher />
                <UserMenu />
              </div>
            </div>
          </div>
        ) : (
          // 비인증 사용자: CTA 배너 (+ 데스크톱 전용 LanguageSwitcher, 모바일은 MobileHeader에 있음)
          <div className="py-3 lg:py-4 px-4 lg:px-8 shrink-0 bg-primary/5 border-b border-primary/10 flex items-center">
            <div className="w-full max-w-5xl mx-auto flex items-center justify-between gap-4">
              <div className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                <p className="text-sm font-medium text-slate-700 text-center sm:text-left">
                  {t('pages.shareCtaJoin')}
                </p>
                <a
                  href={config?.loginUrl ?? WRKS_PORTAL_URLS.wrks}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors shrink-0"
                >
                  {t('pages.shareCtaJoinButton')}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <div className="hidden lg:block">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}

        {/* 공유 비디오 표시 영역 */}
        <div className="flex-1 flex items-center justify-center p-4 lg:p-8 overflow-auto">
          <div className="max-w-3xl w-full">
            <VideoPlayer
              src={sharedDetail.videoUrl}
              poster={sharedDetail.thumbnailUrls?.[0] ?? sharedDetail.thumbnailUrl ?? undefined}
              controls
              className="w-full h-auto rounded-lg shadow-lg"
              style={{
                aspectRatio: sharedDetail.ratio.replace(':', '/'),
              }}
            />
            <AiGeneratedNotice className="mt-3 justify-center" />
          </div>
        </div>
      </main>
    </div>
  )
}
